import { useMemo, useState } from "react";
import { AlertTriangle, Search } from "lucide-react";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);
const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
const FLAG_LABEL = { pre_event: "pre-event", out_of_window: "out of window", duplicate: "duplicate", indirect: "indirect",
  day_labour_not_opt_in: "day labour — no opt-in", before_opt_in: "before opt-in date",
  payroll_tax: "payroll tax", manual_journal: "manual journal", capital_purchase: "capital purchase",
  ineligible_role: "ineligible role", plant_overhead: "plant overhead" };
const STATUS_STYLE = {
  eligible: "border-emerald-400/50 text-emerald-300", ineligible: "border-red-400/50 text-red-300",
  flagged: "border-amber-400/50 text-amber-300",
};
const PAGE = 50;
const BULK_REASON = {
  pre_event: "Invoice dated prior to the event — ineligible under DRFA.",
  duplicate: "Duplicate invoice — same invoice number and amount.",
  out_of_window: "Outside the completion window — no approved extension on file.",
  day_labour_not_opt_in: "Ordinary-hours day labour is not claimable — the council has not opted into the day-labour co-funding arrangements (NSWRA fact sheet).",
  before_opt_in: "Labour incurred before the council's opt-in effective date — not claimable under the co-funding arrangements.",
  payroll_tax: "State-imposed payroll tax — not claimable.",
  manual_journal: "Manual journal / accrual without substantiation — invoice, evidence or written explanation required.",
  capital_purchase: "Capital purchase (plant, tools, signage or equipment) — not claimable.",
  ineligible_role: "Personnel not directly engaged in the restoration works — not claimable.",
  plant_overhead: "Plant overhead beyond cost recovery (insurance, licence, profit, garaging, risk allowance, administration) — not claimable.",
};

function MeasureGroup({ g, data, readOnly, onLine, onBulk }) {
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [showAll, setShowAll] = useState(false);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return g.lines.filter((x) =>
      (filter === "all" || (filter === "flagged" && x.flags.length > 0 && x.status !== "ineligible")
        || (filter === "ineligible" && x.status === "ineligible") || (filter === "labour" && x.is_labour)) &&
      (!needle || `${x.description} ${x.invoice_no} ${x.supplier} ${x.work_order}`.toLowerCase().includes(needle)));
  }, [g.lines, filter, q]);
  const visible = showAll ? filtered : filtered.slice(0, PAGE);

  const bulkTargets = (flag) => g.lines.filter((x) => x.flags.includes(flag) && x.status !== "ineligible");
  const unresolvedFlagged = g.lines.filter((x) => x.status === "flagged");

  const counts = {
    all: g.lines.length,
    flagged: g.lines.filter((x) => x.flags.length > 0 && x.status !== "ineligible").length,
    ineligible: g.lines.filter((x) => x.status === "ineligible").length,
    labour: g.lines.filter((x) => x.is_labour).length,
  };

  return (
    <div className="border border-gold-500/20 bg-navy-900/30" data-testid={`fin-measure-${g.measure}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-b border-gold-500/15">
        <span className="font-heading text-lg text-gold-300">{g.measure}</span>
        <Mono className="text-[0.5rem] text-white/40">Completion window to {g.window_end} · {g.lines.length} lines{g.flagged_unresolved ? ` · ${g.flagged_unresolved} unresolved flags` : ""}</Mono>
      </div>

      <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-white/5">
        {["all", "flagged", "ineligible", "labour"].map((k) => (
          <button key={k} onClick={() => { setFilter(k); setShowAll(false); }} data-testid={`fin-filter-${g.measure}-${k}`}
            className={`border px-2.5 py-1 text-[0.65rem] transition-colors ${filter === k ? "border-gold-500 text-gold-300 bg-gold-500/10" : "border-gold-500/20 text-white/45 hover:text-gold-300"}`}>
            {k} ({counts[k]})
          </button>
        ))}
        <div className="relative">
          <Search className="h-3 w-3 text-white/30 absolute left-2 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setShowAll(false); }} placeholder="Search description, invoice, supplier…"
            data-testid={`fin-search-${g.measure}`}
            className="bg-navy-950 border border-gold-500/25 rounded-sm pl-7 pr-2 py-1 text-xs w-64 focus:outline-none focus:border-gold-500/60" />
        </div>
        {!readOnly && (
          <div className="flex flex-wrap gap-2 ml-auto">
            {["pre_event", "duplicate", "out_of_window", "day_labour_not_opt_in", "before_opt_in"].map((flag) => {
              const n = bulkTargets(flag).length;
              if (!n) return null;
              return (
                <button key={flag} data-testid={`fin-bulk-${g.measure}-${flag}`}
                  onClick={() => onBulk(bulkTargets(flag).map((x) => ({ id: x.id, status: "ineligible", reason: BULK_REASON[flag] })))}
                  className="border border-red-400/40 text-red-300 px-2.5 py-1 text-[0.65rem] hover:bg-red-400/10">
                  Rule {n} {FLAG_LABEL[flag]} → ineligible
                </button>
              );
            })}
            {unresolvedFlagged.length > 0 && (
              <button data-testid={`fin-bulk-${g.measure}-accept`}
                onClick={() => onBulk(unresolvedFlagged.map((x) => ({ id: x.id, status: "eligible", reason: x.reason || "Reviewed — flag resolved, expenditure eligible." })))}
                className="border border-emerald-400/40 text-emerald-300 px-2.5 py-1 text-[0.65rem] hover:bg-emerald-400/10">
                Accept {unresolvedFlagged.length} remaining flagged → eligible
              </button>
            )}
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead><tr className="text-left border-b border-white/10">
            {["Date", "Description", "Invoice", "Amount", "Flags", "Ruling", "Reason"].map((h) => (
              <th key={h} className="py-1.5 px-3"><Mono className="text-[0.5rem] text-white/35">{h}</Mono></th>
            ))}
          </tr></thead>
          <tbody>
            {visible.map((x) => (
              <tr key={x.id} className="border-b border-white/5 align-top" data-testid={`fin-line-${x.id.slice(0, 8)}`}>
                <td className="py-2 px-3 font-mono text-xs whitespace-nowrap">{x.date}</td>
                <td className="py-2 px-3 text-white/80 text-xs">{x.description}
                  {x.is_overtime ? <Mono className="text-[0.45rem] text-amber-300 border border-amber-400/40 px-1 py-0.5 ml-1.5">OT · WC only</Mono>
                    : x.is_labour ? <Mono className="text-[0.45rem] text-sky-300 border border-sky-400/40 px-1 py-0.5 ml-1.5">labour</Mono> : null}
                  <span className="text-white/30 block text-[0.65rem]">{x.supplier}</span></td>
                <td className="py-2 px-3 font-mono text-xs text-white/45">{x.invoice_no || (x.is_labour ? <span className="text-white/30 normal-case">internal · {x.work_order}</span> : "")}</td>
                <td className="py-2 px-3 font-mono text-xs">{money(x.amount)}</td>
                <td className="py-2 px-3">{x.flags.map((f) => (
                  <Mono key={f} className="text-[0.45rem] text-amber-300 border border-amber-400/40 px-1 py-0.5 mr-1 inline-flex items-center gap-1"><AlertTriangle className="h-2.5 w-2.5" />{FLAG_LABEL[f] || f}</Mono>
                ))}</td>
                <td className="py-2 px-3">
                  {readOnly ? (
                    <Mono className={`text-[0.5rem] border px-1.5 py-0.5 ${STATUS_STYLE[x.status]}`}>{x.status}</Mono>
                  ) : (
                    <select value={x.status} onChange={(e) => onLine(x.id, { status: e.target.value, reason: x.reason })}
                      data-testid={`fin-status-${x.id.slice(0, 8)}`}
                      className={`bg-navy-950 border rounded-sm px-2 py-1 text-xs ${STATUS_STYLE[x.status]}`}>
                      <option value="eligible">eligible</option><option value="flagged">flagged</option><option value="ineligible">ineligible</option>
                    </select>
                  )}
                </td>
                <td className="py-2 px-3">
                  {readOnly ? <span className="text-white/50 font-light text-xs">{x.reason || "—"}</span> : (
                    <input value={x.reason || ""} onChange={(e) => onLine(x.id, { status: x.status, reason: e.target.value })}
                      placeholder="Reason…" data-testid={`fin-reason-${x.id.slice(0, 8)}`}
                      className="bg-navy-950 border border-gold-500/25 rounded-sm px-2 py-1 text-xs w-full min-w-[140px] focus:outline-none focus:border-gold-500/60" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filtered.length > visible.length && (
        <button onClick={() => setShowAll(true)} data-testid={`fin-showall-${g.measure}`}
          className="w-full py-2 text-xs text-gold-300 hover:bg-gold-500/5 border-t border-white/5">
          Show all {filtered.length} lines (showing first {PAGE})
        </button>
      )}
      <div className="flex flex-wrap gap-5 px-4 py-3 border-t border-gold-500/15 text-xs" data-testid={`fin-totals-${g.measure}`}>
        <span className="text-white/45">Gross <span className="font-mono text-white block">{money(g.gross)}</span></span>
        <span className="text-white/45">Ineligible <span className="font-mono text-red-300 block">{money(g.ineligible)}</span></span>
        <span className="text-white/45">Net direct <span className="font-mono text-white block">{money(g.net_direct)}</span></span>
        <span className="text-white/45">Labour base <span className="font-mono text-sky-300 block">{money(g.labour_base)}</span></span>
        <span className="text-white/45">On-cost {data.oncost_mode === "manual" ? `${data.oncost_pct}%` : "official FY rates"} <span className={`font-mono block ${g.oncost_over_cap ? "text-amber-300" : "text-emerald-300"}`}>{money(g.oncost)}{g.oncost_over_cap ? " · over cap — capped" : ""}</span></span>
        <span className="text-white/45">Final claimable <span className="font-mono text-gold-300 block">{money(g.final)}</span></span>
      </div>
    </div>
  );
}

export default function FinalisationPanel({ data, readOnly = false, onLine, onOncost, onBulk }) {
  if (!data) return null;
  if (!data.has_ledger) {
    return <p className="text-white/40 font-light text-sm border border-dashed border-white/10 p-6 text-center" data-testid="finalisation-empty">
      No ledger lines are matched to this claim yet — import the council ledger to run finalisation.</p>;
  }
  const gr = data.grand;
  return (
    <div className="space-y-5" data-testid="finalisation-panel">
      <div className="flex flex-wrap items-center gap-4 border border-gold-500/20 bg-navy-950/50 p-3">
        <Mono className="text-[0.55rem] text-gold-400">On-costs — official NSWRA rates by financial year</Mono>
        {readOnly ? (
          <Mono className="text-[0.5rem] text-white/60 border border-gold-500/25 px-2 py-1">
            {data.oncost_mode === "manual" ? `Manual ${data.oncost_pct}%` : data.oncost_mode === "state_agency" ? "Official — NSW state agency" : "Official — opt-in council"}
          </Mono>
        ) : (
          <select value={data.oncost_mode} onChange={(e) => onOncost && onOncost({ oncost_mode: e.target.value })}
            data-testid="oncost-mode"
            className="bg-navy-950 border border-gold-500/25 rounded-sm px-2 py-1 text-xs">
            <option value="opt_in_council">Official — opt-in council</option>
            <option value="state_agency">Official — NSW state agency</option>
            <option value="manual">Manual rate</option>
          </select>
        )}
        {data.oncost_mode === "manual" ? (
          <label className="text-xs text-white/50 flex items-center gap-1.5">Rate %
            <input type="number" value={data.oncost_pct} disabled={readOnly} data-testid="oncost-pct"
              onChange={(e) => onOncost && onOncost({ oncost_pct: Number(e.target.value) })}
              className="bg-navy-950 border border-gold-500/25 rounded-sm px-2 py-1 text-xs w-16 font-mono disabled:opacity-70" />
          </label>
        ) : (
          <span className="font-mono text-xs text-emerald-300" data-testid="oncost-official-rate">
            FY rate applied per line · e.g. FY2025-26 = {data.official_rates?.[data.oncost_mode]?.["2025-26"]}%
          </span>
        )}
        {data.manual_above_official && (
          <Mono className="text-[0.5rem] text-amber-300 border border-amber-400/40 px-1.5 py-0.5" data-testid="oncost-above-official">
            Above published rate ({data.event_official_rate}%) — supporting documentation required
          </Mono>
        )}
        <span className="border-l border-white/10 pl-4 flex items-center gap-2">
          {readOnly ? (
            <Mono className={`text-[0.5rem] border px-2 py-1 ${data.opt_in ? "border-emerald-400/40 text-emerald-300" : "border-red-400/40 text-red-300"}`}>
              {data.opt_in ? "Opted-in — day labour co-funded" : "NOT opted-in — day labour not claimable"}
            </Mono>
          ) : (
            <>
              <select value={data.opt_in ? "yes" : "no"} onChange={(e) => onOncost && onOncost({ opt_in: e.target.value === "yes" })}
                data-testid="optin-select"
                className="bg-navy-950 border border-gold-500/25 rounded-sm px-2 py-1 text-xs">
                <option value="yes">Council opted-in (day labour co-funded)</option>
                <option value="no">Not opted-in — day labour not claimable</option>
              </select>
              {data.opt_in && (
                <label className="text-xs text-white/50 flex items-center gap-1.5">Effective
                  <input type="date" value={data.opt_in_effective || ""} data-testid="optin-effective"
                    onChange={(e) => onOncost && onOncost({ opt_in_effective: e.target.value })}
                    className="bg-navy-950 border border-gold-500/25 rounded-sm px-2 py-1 text-xs font-mono" />
                </label>
              )}
              <span className="text-white/30 text-[0.6rem] font-light">save to re-run checks</span>
            </>
          )}
        </span>
        <span className="text-white/35 text-[0.65rem] font-light w-full">{data.oncost_source} · base = ordinary time earnings · {data.oncost_components} · rates change each FY — confirm annually. Computed from the ledger labour base, never a separate salary spreadsheet.</span>
      </div>

      {data.measures.map((g) => (
        <MeasureGroup key={g.measure} g={g} data={data} readOnly={readOnly} onLine={onLine} onBulk={onBulk || (() => {})} />
      ))}

      <div className="border border-gold-500/40 bg-gold-500/5 p-4 flex flex-wrap gap-6 text-sm" data-testid="fin-grand">
        <span>Gross ledger <span className="font-mono text-white block">{money(gr.gross)}</span></span>
        <span>Ineligible <span className="font-mono text-red-300 block">{money(gr.ineligible)}</span></span>
        <span>Net direct <span className="font-mono text-white block">{money(gr.net_direct)}</span></span>
        <span>On-costs applied <span className="font-mono text-emerald-300 block">{money(gr.oncost)}</span></span>
        <span>Final claimable <span className="font-mono text-gold-300 text-base block" data-testid="fin-final">{money(gr.final)}</span></span>
      </div>
    </div>
  );
}
